import React from 'react';
import { Card } from '../ui/card';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const COLORS = ['#1e40af', '#059669', '#dc2626', '#ea580c', '#7c3aed', '#0891b2', '#f59e0b'];

const KPIPiattaforme = ({ bookings, filters }) => {
  const filterBookings = (bookingsList) => {
    return bookingsList.filter(booking => {
      const bookingDate = new Date(booking.booking_date);
      const bookingYear = bookingDate.getFullYear();
      const bookingMonth = bookingDate.getMonth() + 1;
      const bookingQuarter = Math.ceil(bookingMonth / 3);
      if (bookingYear !== filters.year) return false;
      if (filters.period === 'month' && filters.month) return bookingMonth === parseInt(filters.month);
      if (filters.period === 'quarter' && filters.quarter) return bookingQuarter === parseInt(filters.quarter);
      if (filters.propertyId && booking.property_id !== filters.propertyId) return false;
      return true;
    });
  };

  const currentBookings = filterBookings(bookings);

  const calculatePaymentStatus = (booking) => {
    const code = (booking.booking_code || booking.code || '').toUpperCase();
    const platform = (booking.platform || '').toLowerCase();
    const checkIn = new Date(booking.check_in);
    const today = new Date();
    
    if (!booking.check_in) return 'pending';
    
    // Codice contiene 'N'
    if (code.includes('N')) {
      if (['booking', 'booking_com', 'booking.com'].includes(platform)) {
        return 'paid';  // Incassate subito
      } else if (platform === 'holiday_world') {
        // Holiday World con N: dipende da quanti giorni tra booking e check-in
        const bookingDate = new Date(booking.booking_date);
        const daysDiff = Math.floor((checkIn - bookingDate) / (1000 * 60 * 60 * 24));
        
        if (daysDiff < 30) {
          // Meno di 30 giorni: tutto incassato subito
          return today >= bookingDate ? 'paid' : 'pending';
        } else {
          // Più di 30 giorni: 30% subito, 70% a 30 giorni prima check-in
          const paymentDateFull = new Date(checkIn);
          paymentDateFull.setDate(paymentDateFull.getDate() - 30);
          
          if (today >= paymentDateFull) {
            return 'paid';  // Tutto incassato
          } else if (today >= bookingDate) {
            return 'partial';  // 30% incassato
          } else {
            return 'pending';  // Niente incassato ancora
          }
        }
      } else if (platform === 'atraveo') {
        // Pagate 2 settimane prima del check-in
        const paymentDate = new Date(checkIn);
        paymentDate.setDate(paymentDate.getDate() - 14);
        return today >= paymentDate ? 'paid' : 'pending';
      } else if (platform === 'airbnb' || platform === 'altri') {
        // Sempre pending, pagate 3 giorni dopo check-in
        return 'pending';
      }
      return 'pending';
    }
    
    // Codice contiene 'R'
    if (code.includes('R')) {
      if (['booking', 'booking_com', 'booking.com', 'holiday_world'].includes(platform)) {
        // Pagate 5 giorni prima del check-in (Booking e Holiday World con R)
        const paymentDate = new Date(checkIn);
        paymentDate.setDate(paymentDate.getDate() - 5);
        return today >= paymentDate ? 'paid' : 'pending';
      }
      return 'pending';
    }
    
    // Se ha già un payment_status, usa quello
    return booking.payment_status || 'pending';
  };

  const getBookingsByPlatform = () => {
    const grouped = {};
    currentBookings.forEach(booking => {
      const platform = booking.platform || 'unknown';
      if (!grouped[platform]) {
        grouped[platform] = { 
          name: platform, 
          count: 0, 
          revenue: 0,
          incassato: 0,
          daIncassare: 0
        };
      }
      grouped[platform].count++;
      const guestTotal = booking.guest_total || 0;
      grouped[platform].revenue += guestTotal;
      
      // Calculate payment status using the logic
      const paymentStatus = calculatePaymentStatus(booking);
      
      // Calculate incassato (paid) and da incassare (pending/partial)
      if (paymentStatus === 'paid') {
        grouped[platform].incassato += guestTotal;
      } else if (paymentStatus === 'pending') {
        grouped[platform].daIncassare += guestTotal;
      } else if (paymentStatus === 'partial') {
        // For Holiday World with N and >30 days: 30% incassato, 70% da incassare
        grouped[platform].incassato += guestTotal * 0.30;
        grouped[platform].daIncassare += guestTotal * 0.70;
      }
    });
    return Object.values(grouped);
  };

  const platformData = getBookingsByPlatform();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">🌐 Analisi Piattaforme</h2>
        <p className="text-gray-600">Performance per canale di acquisizione</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🌐 Distribuzione per Piattaforma</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={platformData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {platformData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">💰 Ricavi per Piattaforma</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={platformData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
              <Legend />
              <Bar dataKey="revenue" fill="#059669" name="Ricavi Totali (€)" />
              <Bar dataKey="count" fill="#1e40af" name="Prenotazioni" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Incassi e Da Incassare */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">✅ Incassato per Piattaforma</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={platformData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
              <Legend />
              <Bar dataKey="incassato" fill="#10b981" name="Incassato (€)" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">⏳ Da Incassare per Piattaforma</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={platformData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
              <Legend />
              <Bar dataKey="daIncassare" fill="#f59e0b" name="Da Incassare (€)" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Tabella Dettagliata */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Dettaglio Incassi per Piattaforma</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Piattaforma</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Prenotazioni</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">Ricavi Totali</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">✅ Incassato</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">⏳ Da Incassare</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">% Incassato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {platformData.map((platform, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{platform.name}</td>
                  <td className="py-3 px-4 text-center text-gray-700">{platform.count}</td>
                  <td className="py-3 px-4 text-right text-gray-900">
                    €{platform.revenue.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="py-3 px-4 text-right text-green-600 font-semibold">
                    €{platform.incassato.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="py-3 px-4 text-right text-orange-600 font-semibold">
                    €{platform.daIncassare.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-700">
                    {platform.revenue > 0 ? ((platform.incassato / platform.revenue) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
              {platformData.length > 0 && (
                <tr className="bg-gray-100 font-bold">
                  <td className="py-3 px-4 text-gray-900">TOTALE</td>
                  <td className="py-3 px-4 text-center text-gray-900">
                    {platformData.reduce((sum, p) => sum + p.count, 0)}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">
                    €{platformData.reduce((sum, p) => sum + p.revenue, 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="py-3 px-4 text-right text-green-600">
                    €{platformData.reduce((sum, p) => sum + p.incassato, 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="py-3 px-4 text-right text-orange-600">
                    €{platformData.reduce((sum, p) => sum + p.daIncassare, 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">
                    {platformData.reduce((sum, p) => sum + p.revenue, 0) > 0 
                      ? ((platformData.reduce((sum, p) => sum + p.incassato, 0) / platformData.reduce((sum, p) => sum + p.revenue, 0)) * 100).toFixed(1) 
                      : 0}%
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default KPIPiattaforme;